import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Website, BackupStatus } from '../types';
import { websiteService, backupService, probeService } from '../services/api';
import { XCircleIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import CategorizedComments from '../components/ui/CategorizedComments';

const WebsiteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [website, setWebsite] = useState<Website | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('general');
  const [deleting, setDeleting] = useState(false);
  const [checkingBackup, setCheckingBackup] = useState(false);
  const [detectingPlugins, setDetectingPlugins] = useState(false);
  const [pluginsData, setPluginsData] = useState<any | null>(null);
  
  // État pour la modal de confirmation de suppression
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Charger les données du site web depuis l'API
  useEffect(() => {
    const fetchWebsite = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await websiteService.getById(id);
        setWebsite(data);
        setError(null);
      } catch (err: any) {
        console.error('Erreur lors du chargement du site :', err);
        setError(err.response?.data?.message || 'Impossible de charger les données du site. Veuillez réessayer plus tard.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchWebsite();
  }, [id]);

  useEffect(() => {
    // Afficher les informations de débogage du site
    if (website && process.env.NODE_ENV === 'development') {
      console.log('Détail du site:');
      console.log(`Nom: ${website.clientName}`);
      console.log(`État du contrat (status): ${website.status}`);
      console.log(`Disponibilité HTTP (httpStatus): ${website.httpStatus}`);
      console.log(`Dernière vérification: ${website.lastChecked}`);
    }
  }, [website]);

  const handleDelete = async () => {
    setDeleteModalOpen(true);
  };

  // Fonction pour confirmer la suppression
  const confirmDelete = async () => {
    if (!id) return;
    
    try {
      setDeleting(true);
      await websiteService.delete(id);
      navigate('/sites');
    } catch (err: any) {
      console.error('Erreur lors de la suppression du site :', err);
      setError(err.response?.data?.message || 'Une erreur est survenue lors de la suppression du site. Veuillez réessayer.');
      setDeleting(false);
    }
  };

  // Fonction pour annuler la suppression
  const cancelDelete = () => {
    setDeleteModalOpen(false);
  };

  // Vérifier le backup du site
  const checkBackup = async () => {
    if (!id) return;
    
    try {
      setCheckingBackup(true);
      const result = await backupService.checkWebsiteBackup(id);
      
      // Rafraîchir les données du site pour avoir les informations de backup à jour
      const updatedWebsite = await websiteService.getById(id);
      setWebsite(updatedWebsite);
      
      setError(null);
    } catch (err: any) {
      console.error('Erreur lors de la vérification du backup :', err);
      setError(err.response?.data?.message || 'Impossible de vérifier le backup. Veuillez réessayer plus tard.');
    } finally {
      setCheckingBackup(false);
    }
  };

  // Nouvelle fonction pour détecter les plugins WordPress
  const detectWordPressPlugins = async () => {
    if (!id) return;
    
    try {
      setDetectingPlugins(true);
      setError(null);
      const result = await probeService.detectWordPressPlugins(id);
      setPluginsData(result);
      
      // Rafraîchir les données du site pour avoir les informations à jour
      const updatedWebsite = await websiteService.getById(id);
      setWebsite(updatedWebsite);
      
    } catch (err: any) {
      console.error('Erreur lors de la détection des plugins WordPress :', err);
      setError(err.response?.data?.message || 'Impossible de détecter les plugins WordPress. Veuillez réessayer plus tard.');
    } finally {
      setDetectingPlugins(false);
    }
  };

  // Fonction pour déterminer la classe de couleur en fonction de l'âge du backup
  const getBackupDateColorClass = (dateStr: string | undefined): string => {
    if (!dateStr) return 'text-gray-400'; // Non disponible
    
    const backupDate = new Date(dateStr);
    const now = new Date();
    const diffHours = (now.getTime() - backupDate.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return 'text-green-600 dark:text-green-400 font-medium';
    } else if (diffHours < 72) {
      return 'text-yellow-600 dark:text-yellow-400 font-medium';
    } else {
      return 'text-red-600 dark:text-red-400 font-medium';
    }
  };

  // Fonction pour déterminer si le site utilise Minio avec un bucket fonctionnel
  const usesMinio = () => {
    if (!website) return false;
    
    return website.backupType === 'minio' && 
           website.bucketName && 
           website.backupStatus && 
           website.backupStatus !== 'not_found';
  };

  // Si le site est en cours de chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Si une erreur est survenue lors du chargement
  if (error && !website) {
    return (
      <div className="bg-red-100 dark:bg-red-900 p-4 rounded-md text-red-800 dark:text-red-200">
        <p>{error}</p>
        <button 
          onClick={() => navigate('/sites')} 
          className="mt-4 btn-primary"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  // Si le site n'existe pas
  if (!website) {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Site non trouvé</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">Le site web que vous recherchez n'existe pas ou a été supprimé.</p>
        <Link to="/sites" className="btn-primary">
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <Link to="/sites" className="text-primary-600 dark:text-primary-400 hover:underline flex items-center mb-2">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la liste
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{website.clientName}</h1>
          <a 
            href={website.url} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            {website.url}
          </a>
        </div>
        
        <div className="flex space-x-2">
          <Link to={`/sites/${id}/edit`} className="btn-primary">
            Modifier
          </Link>
          <button 
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleting ? 'Suppression...' : 'Supprimer'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-100 dark:bg-red-900 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">
                {error}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Onglets */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          <button
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'general'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('general')}
          >
            Informations générales
          </button>
          <button
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'hosting'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('hosting')}
          >
            Hébergement
          </button>
          <button
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'billing'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('billing')}
          >
            Facturation
          </button>
          <button
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'domain'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('domain')}
          >
            Domaine et DNS
          </button>
          <button
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'email'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('email')}
          >
            Emails et SMTP
          </button>
          <button
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'maintenance'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('maintenance')}
          >
            Maintenance
          </button>
          <button
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'contact'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('contact')}
          >
            Contacts
          </button>
          <button
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'wordpress'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
            onClick={() => setActiveTab('wordpress')}
          >
            WordPress
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      <div className="card">
        {activeTab === 'general' && (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Informations générales</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nom du client</p>
                  <p className="mt-1">{website.clientName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">URL</p>
                  <p className="mt-1">
                    <a href={website.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {website.url}
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Plateforme</p>
                  <p className="mt-1">
                    {website.platform}
                    {website.detectionResults && 
                     website.detectionResults.wordpress && 
                     website.detectionResults.wordpress.subProbes && 
                     website.detectionResults.wordpress.subProbes.mainwp && (
                      <>
                        {website.detectionResults.wordpress.subProbes.mainwp.detected && (
                          <span className="ml-2 inline-flex items-center">
                            <img 
                              src="/icons/mainwp-icon.svg" 
                              alt="Géré par MainWP" 
                              className="w-5 h-5 inline"
                              aria-label="Ce site est géré par MainWP"
                            />
                            <span className="ml-1 text-sm text-green-600 dark:text-green-400">
                              Géré par MainWP
                            </span>
                          </span>
                        )}
                        
                        {website.detectionResults.wordpress.detected && 
                         !website.detectionResults.wordpress.subProbes.mainwp.detected && 
                         website.detectionResults.wordpress.subProbes.mainwp.missingMainWP && (
                          <span className="ml-2 inline-flex items-center">
                            <svg 
                              xmlns="http://www.w3.org/2000/svg" 
                              viewBox="0 0 24 24" 
                              className="w-5 h-5 inline" 
                              fill="#ff4d4d"
                              aria-label="Site WordPress sans MainWP détecté"
                            >
                              <path d="M22 6.2c0-1.7-1.4-3.1-3.1-3.1H5.1C3.4 3.1 2 4.5 2 6.2v11.6c0 1.7 1.4 3.1 3.1 3.1h13.7c1.7 0 3.1-1.4 3.1-3.1V6.2zM9.5 16.4h-2v-5.8h2v5.8zm3.5 0h-2V7.6h2v8.8zm3.5 0h-2v-3.9h2v3.9z"/>
                            </svg>
                            <span className="ml-1 text-sm text-red-600 dark:text-red-400">
                              WordPress non géré par MainWP
                            </span>
                          </span>
                        )}
                      </>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Date de création</p>
                  <p className="mt-1">{website.creationDate}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Date d'anniversaire</p>
                  <p className="mt-1">{website.anniversaryDate}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">État du contrat</p>
                  <p className="mt-1">
                    <span 
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        website.status === 'active' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                          : website.status === 'dormant'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                            : website.status === 'production'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                              : website.status === 'internal'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                      }`}
                    >
                      {website.status === 'active' 
                        ? 'Actif' 
                        : website.status === 'dormant' 
                          ? 'En sommeil' 
                          : website.status === 'production'
                            ? 'En production'
                            : website.status === 'internal'
                              ? 'Projet Interne'
                              : 'Terminé'}
                    </span>
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Gestion des cookies</p>
                  <div className="mt-1 flex items-center">
                    <p>{website.cookiesManagement || 'Non spécifié'}</p>
                    
                    {/* Afficher l'icône Cookies selon la logique demandée */}
                    {(() => {
                      const cookiesData = website.detectionResults?.wordpress?.subProbes?.cookies;
                      const hasCookiesData = cookiesData !== undefined;
                      const detectedPlugin = cookiesData?.detectedPlugin;
                      const configuredPlugin = website.cookiesManagement;
                      const cookiesStatus = cookiesData?.status;
                      
                      // Si on a des données de détection cookies
                      if (hasCookiesData && cookiesStatus) {
                        let iconSrc = '';
                        let altText = '';
                        let titleText = '';
                        
                        switch (cookiesStatus) {
                          case 'match':
                            // Vert : même plugin que celui configuré
                            iconSrc = '/icons/cookies-icon-green.svg';
                            altText = 'Plugin cookies conforme';
                            titleText = `Plugin cookies conforme : ${detectedPlugin?.name || 'Plugin détecté'} correspond à la configuration`;
                            break;
                          case 'mismatch':
                            // Orange : plugin différent de celui configuré
                            iconSrc = '/icons/cookies-icon-orange.svg';
                            altText = 'Plugin cookies différent';
                            titleText = `Plugin cookies différent : ${detectedPlugin?.name || 'Plugin détecté'} ne correspond pas à la configuration (${configuredPlugin})`;
                            break;
                          case 'missing':
                            // Rouge : aucun plugin détecté alors qu'un gestionnaire est configuré
                            iconSrc = '/icons/cookies-icon-red.svg';
                            altText = 'Plugin cookies manquant';
                            titleText = `Plugin cookies manquant : aucun plugin détecté mais ${configuredPlugin} est configuré`;
                            break;
                          case 'unspecified_detected':
                            // Gris : plugin détecté mais utilisateur n'a pas spécifié de gestionnaire
                            iconSrc = '/icons/cookies-icon-gray.svg';
                            altText = 'Plugin cookies non spécifié';
                            titleText = `Plugin cookies détecté : ${detectedPlugin?.name || 'Plugin détecté'} mais aucun gestionnaire spécifié dans la configuration`;
                            break;
                          default:
                            return null;
                        }
                        
                        return (
                          <img 
                            src={iconSrc}
                            alt={altText}
                            className="w-5 h-5 ml-2"
                            aria-label={titleText}
                            title={titleText}
                          />
                        );
                      }
                      
                      // Si pas de données cookies, ne pas afficher d'icône
                      return null;
                    })()}
                  </div>
                  
                  {/* Afficher le statut détaillé des cookies */}
                  {website.detectionResults?.wordpress?.subProbes?.cookies && (
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {(() => {
                        const cookiesData = website.detectionResults.wordpress.subProbes.cookies;
                        const detectedPlugin = cookiesData.detectedPlugin;
                        const configuredPlugin = website.cookiesManagement;
                        const cookiesStatus = cookiesData.status;
                        
                        switch (cookiesStatus) {
                          case 'match':
                            return <span className="text-green-600 dark:text-green-400">✓ Plugin conforme : {detectedPlugin?.name}</span>;
                          case 'mismatch':
                            return <span className="text-orange-600 dark:text-orange-400">⚠ Plugin différent : {detectedPlugin?.name} (configuré : {configuredPlugin})</span>;
                          case 'missing':
                            return <span className="text-red-600 dark:text-red-400">⚠ Plugin manquant : aucun plugin détecté (configuré : {configuredPlugin})</span>;
                          case 'unspecified_detected':
                            return <span className="text-gray-600 dark:text-gray-400">Plugin détecté : {detectedPlugin?.name} (non spécifié dans la configuration)</span>;
                          case 'unspecified_none':
                            return <span className="text-gray-600 dark:text-gray-400">Aucun plugin de cookies détecté</span>;
                          default:
                            return <span className="text-gray-600 dark:text-gray-400">Statut inconnu</span>;
                        }
                      })()}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Type de Captcha</p>
                  <div className="mt-1 flex items-center">
                    <p>{website.captchaType || website.authType || 'Non spécifié'}</p>
                    
                    {/* Afficher l'icône Turnstile selon la logique demandée */}
                    {website.captchaType === 'Turnstile' && (() => {
                      const turnstileDetected = website.detectionResults?.wordpress?.subProbes?.turnstile?.detected === true;
                      const isConfigured = website.detectionResults?.wordpress?.subProbes?.turnstile?.isConfigured === true || 
                                         website.detectionResults?.wordpress?.subProbes?.turnstile?.isConfigured === 1;
                      
                      // Chercher le plugin Turnstile dans la liste des plugins MainWP
                      const mainwpPlugins = website.detectionResults?.wordpress?.subProbes?.mainwp?.plugins || [];
                      const turnstilePlugin = mainwpPlugins.find((plugin: any) => 
                        plugin.slug && plugin.slug.includes('turnstile')
                      );
                      
                      // Si on a des données MainWP, utiliser le statut MainWP
                      if (turnstilePlugin) {
                        const turnstileActive = turnstilePlugin.active === 1;
                        // Si le plugin est détecté ET actif ET configuré → icône verte
                        if (turnstileDetected && turnstileActive && isConfigured) {
                          return (
                            <img 
                              src="/icons/turnstile-icon-green.svg" 
                              alt="Turnstile actif" 
                              className="w-5 h-5 ml-2"
                              aria-label="Protection Turnstile active et configurée"
                              title="Turnstile est correctement installé, activé et configuré"
                            />
                          );
                        }
                        // Si captchaType=Turnstile mais plugin désactivé/absent/non configuré → icône rouge
                        else {
                          return (
                            <img 
                              src="/icons/turnstile-icon-red.svg" 
                              alt="Turnstile inactif" 
                              className="w-5 h-5 ml-2"
                              aria-label="Protection Turnstile désactivée ou non configurée"
                              title="Turnstile est configuré mais le plugin est désactivé ou non configuré"
                            />
                          );
                        }
                      }
                      // Si pas de données MainWP, se baser uniquement sur la détection HTML/JS
                      else {
                        // Si le plugin est détecté via HTML/JS → icône verte
                        if (turnstileDetected) {
                          return (
                            <img 
                              src="/icons/turnstile-icon-green.svg" 
                              alt="Turnstile actif" 
                              className="w-5 h-5 ml-2"
                              aria-label="Protection Turnstile active et configurée"
                              title="Turnstile détecté et actif"
                            />
                          );
                        }
                        // Si captchaType=Turnstile mais plugin non détecté → icône rouge
                        else {
                          return (
                            <img 
                              src="/icons/turnstile-icon-red.svg" 
                              alt="Turnstile inactif" 
                              className="w-5 h-5 ml-2"
                              aria-label="Protection Turnstile désactivée ou non configurée"
                              title="Turnstile configuré mais non détecté"
                            />
                          );
                        }
                      }
                    })()}
                  </div>
                  
                  {/* Afficher le statut détaillé de Turnstile */}
                  {website.captchaType === 'Turnstile' && website.detectionResults?.wordpress?.subProbes?.turnstile && (
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {(() => {
                        const turnstileData = website.detectionResults.wordpress.subProbes.turnstile;
                        const turnstileDetected = turnstileData.detected === true;
                        const isConfigured = turnstileData.isConfigured === true || turnstileData.isConfigured === 1;
                        
                        // Vérifier d'abord si on a des informations MainWP
                        const mainwpPlugins = website.detectionResults?.wordpress?.subProbes?.mainwp?.plugins || [];
                        const turnstilePlugin = mainwpPlugins.find((plugin: any) => 
                          plugin.slug && plugin.slug.includes('turnstile')
                        );
                        
                        // Si on a des données MainWP, utiliser le statut MainWP
                        if (turnstilePlugin) {
                          const turnstileActive = turnstilePlugin.active === 1;
                          if (turnstileDetected && turnstileActive && isConfigured) {
                            return <span className="text-green-600 dark:text-green-400">✓ Plugin installé, activé et configuré</span>;
                          } else if (turnstileDetected && !turnstileActive) {
                            return <span className="text-red-600 dark:text-red-400">⚠ Plugin installé mais désactivé</span>;
                          } else if (turnstileDetected && !isConfigured) {
                            return <span className="text-yellow-600 dark:text-yellow-400">⚠ Plugin installé et activé mais non configuré</span>;
                          }
                        }
                        // Si pas de données MainWP, se baser uniquement sur la détection HTML/JS
                        else {
                          if (turnstileDetected && isConfigured) {
                            return <span className="text-green-600 dark:text-green-400">✓ Plugin détecté et configuré</span>;
                          } else if (turnstileDetected) {
                            // Si détecté via HTML/JS, considérer comme fonctionnel même si isConfigured n'est pas défini
                            return <span className="text-green-600 dark:text-green-400">✓ Plugin détecté et actif</span>;
                          }
                        }
                        
                        // Cas par défaut
                        if (!turnstileDetected) {
                          return <span className="text-gray-600 dark:text-gray-400">Plugin non détecté</span>;
                        }
                        
                        return <span className="text-gray-600 dark:text-gray-400">Statut indéterminé</span>;
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>

              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Commentaires</p>
                {website.categorizedComments ? (
                  <CategorizedComments
                    comments={website.categorizedComments}
                    onChange={() => {}} // Lecture seule
                    readOnly={true}
                  />
                ) : website.comments ? (
                  <div className="mt-1 p-4 bg-gray-50 dark:bg-gray-800 rounded-md min-h-[150px]">
                    <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                      Commentaires (ancien format) :
                    </div>
                    <div className="text-gray-900 dark:text-white whitespace-pre-wrap">
                      {website.comments}
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 p-4 bg-gray-50 dark:bg-gray-800 rounded-md min-h-[150px] text-gray-500 dark:text-gray-400 text-center">
                    Aucun commentaire
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'hosting' && (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Hébergement</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Type d'hébergement</p>
                  <p className="mt-1">{website.hostingType}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Fournisseur d'hébergement</p>
                  <p className="mt-1">{website.hostingProvider}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Adresse IP</p>
                  <div className="mt-1 flex items-center">
                    <p>{website.ipAddress}</p>
                    {/* Afficher l'icône Cloudflare si détecté */}
                    {website.detectionResults?.cloudflare?.detected && (
                      <img 
                        src="/icons/cloudflare.svg" 
                        alt="Protégé par Cloudflare" 
                        className="w-5 h-5 ml-2"
                        title="Site protégé par Cloudflare"
                        aria-label="Site protégé par Cloudflare"
                      />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Configuration du serveur</p>
                  <p className="mt-1">{website.serverConfig}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Expiration SSL</p>
                  <p className="mt-1">{website.sslExpiration}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Facturation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Type de contrat</p>
                  <p className="mt-1">{website.contractType}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Périodicité de facturation</p>
                  <p className="mt-1">
                    {website.billingPeriodicity === 'mensuel' && 'Mensuel'}
                    {website.billingPeriodicity === 'trimestriel' && 'Trimestriel'}
                    {website.billingPeriodicity === 'semestriel' && 'Semestriel'}
                    {website.billingPeriodicity === 'annuel' && 'Annuel'}
                    {website.billingPeriodicity === 'autre' && `Autre (${website.billingPeriodicityDays || 0} jours)`}
                    {(!website.billingPeriodicity || website.billingPeriodicity === 'non_specifie') && 'Non spécifié'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Date d'anniversaire</p>
                  <p className="mt-1">
                    {website.anniversaryDate ? new Date(website.anniversaryDate).toLocaleDateString() : 'Non spécifiée'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Montant</p>
                  <p className="mt-1">{website.amount} €</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Dernière facturation</p>
                  <p className="mt-1">{website.lastBillingDate}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Prochaine facturation</p>
                  <p className="mt-1">{website.nextBillingDate}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Statut de paiement</p>
                  <p className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      website.paymentStatus === 'payé' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                      website.paymentStatus === 'en_attente' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                      website.paymentStatus === 'en_retard' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                    }`}>
                      {website.paymentStatus || 'Non spécifié'}
                    </span>
                  </p>
                </div>
              </div>
              
              {/* Informations calculées */}
              {website.nextBillingDate && (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Informations calculées</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Jours restants :</span>
                        <span className="font-medium">
                          {(() => {
                            const today = new Date();
                            const nextBilling = new Date(website.nextBillingDate);
                            const diffTime = nextBilling.getTime() - today.getTime();
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            return diffDays > 0 ? `${diffDays} jours` : `${Math.abs(diffDays)} jours de retard`;
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Statut :</span>
                        <span className="font-medium">
                          {(() => {
                            const today = new Date();
                            const nextBilling = new Date(website.nextBillingDate);
                            const diffDays = Math.ceil((nextBilling.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                            if (diffDays < 0) return '🔴 En retard';
                            if (diffDays <= 15) return '🟡 À envoyer bientôt';
                            return '🟢 Dans les délais';
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'domain' && (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Domaine et DNS</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nom de domaine</p>
                  <p className="mt-1">{website.domain}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Expiration du domaine</p>
                  <p className="mt-1">{website.domainExpiration}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Registrar</p>
                  <p className="mt-1">{website.registrar}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Cloudflare Proxy</p>
                  <p className="mt-1">{website.dnsConfig.cloudflareProxy ? 'Oui' : 'Non'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Cache Headers</p>
                  <p className="mt-1">{website.dnsConfig.cacheHeaders ? 'Oui' : 'Non'}</p>
                </div>
                {website.preprodUrl && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">URL de préproduction</p>
                    <p className="mt-1">
                      <a 
                        href={website.preprodUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-600 hover:underline"
                      >
                        {website.preprodUrl}
                      </a>
                    </p>
                  </div>
                )}
                
                {website.htaccessEnabled && (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-medium mb-3">Protection htaccess</h3>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nom d'utilisateur</p>
                      <p className="mt-1 font-mono">{website.htaccessUsername}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Mot de passe</p>
                      <p className="mt-1 font-mono">{website.htaccessPassword}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Configuration Email</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Type de configuration</p>
                  <div className="mt-1 flex items-center">
                    <p>
                      {website.emailType === 'smtp' ? 'SMTP' : 
                       website.emailType === 'postal-smtp' ? 'Postal SMTP' : 
                       website.emailType === 'postal-http' ? 'Postal HTTP' : 
                       website.emailType === 'autre' ? 'Autre' : 
                       website.emailType === 'aucun' ? 'Sans email' : 
                       website.smtpServer ? 'SMTP (migré)' : 'Non spécifié'}
                    </p>
                    
                    {/* Afficher l'icône SMTP selon la logique demandée */}
                    {(() => {
                      const smtpData = website.detectionResults?.wordpress?.subProbes?.smtp;
                      const hasSMTPData = smtpData !== undefined;
                      const detectedPlugin = smtpData?.detectedPlugin;
                      const configuredEmailType = website.emailType;
                      const smtpStatus = smtpData?.status;
                      
                      // Si "Sans email" configuré, ne rien afficher
                      if (configuredEmailType === 'aucun') {
                        return null;
                      }
                      
                      // Si on a des données de détection SMTP
                      if (hasSMTPData && smtpStatus) {
                        let iconSrc = '';
                        let altText = '';
                        let titleText = '';
                        
                        switch (smtpStatus) {
                          case 'match':
                            // 🟢 Vert : Plugin SMTP Mailer détecté et actif
                            iconSrc = '/icons/smtp-icon-green.svg';
                            altText = 'SMTP Mailer actif';
                            titleText = `SMTP Mailer par naa986 détecté et actif`;
                            break;
                          case 'other_plugin':
                            // 🟠 Orange : Plugin détecté mais c'est un autre que SMTP Mailer naa986
                            iconSrc = '/icons/smtp-icon-orange.svg';
                            altText = 'Autre plugin SMTP';
                            titleText = `Autre plugin SMTP détecté : ${detectedPlugin?.name || 'Plugin SMTP'} (pas SMTP Mailer par naa986)`;
                            break;
                          case 'missing':
                            // 🔴 Rouge : Plugin non détecté ou désactivé mais Type d'email choisi dans la fiche
                            iconSrc = '/icons/smtp-icon-red.svg';
                            altText = 'Plugin SMTP manquant';
                            titleText = `Plugin SMTP manquant ou inactif alors que le type d'email est configuré (${configuredEmailType})`;
                            break;
                          case 'hidden':
                            // Ne rien afficher si "Sans email"
                            return null;
                          default:
                            return null;
                        }
                        
                        return (
                          <img 
                            src={iconSrc}
                            alt={altText}
                            className="w-5 h-5 ml-2"
                            aria-label={titleText}
                            title={titleText}
                          />
                        );
                      }
                      
                      // Si pas de données SMTP, ne pas afficher d'icône
                      return null;
                    })()}
                  </div>
                  
                  {/* Afficher le statut détaillé de SMTP */}
                  {website.detectionResults?.wordpress?.subProbes?.smtp && (
                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {(() => {
                        const smtpData = website.detectionResults.wordpress.subProbes.smtp;
                        const detectedPlugin = smtpData.detectedPlugin;
                        const configuredEmailType = website.emailType;
                        const smtpStatus = smtpData.status;
                        
                        switch (smtpStatus) {
                          case 'match':
                            return <span className="text-green-600 dark:text-green-400">✓ SMTP Mailer par naa986 détecté et actif</span>;
                          case 'other_plugin':
                            return <span className="text-orange-600 dark:text-orange-400">⚠ Autre plugin SMTP détecté : {detectedPlugin?.name} (configuré : {configuredEmailType})</span>;
                          case 'missing':
                            return <span className="text-red-600 dark:text-red-400">⚠ Plugin SMTP manquant ou inactif (configuré : {configuredEmailType})</span>;
                          case 'hidden':
                            return null;
                          default:
                            return <span className="text-gray-600 dark:text-gray-400">Statut SMTP inconnu</span>;
                        }
                      })()}
                    </div>
                  )}
                </div>
                
                {/* Affichage pour SMTP et Postal SMTP ou sites migrés */}
                {(website.emailType === 'smtp' || website.emailType === 'postal-smtp' || (!website.emailType && website.smtpServer)) && (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Serveur SMTP</p>
                      <p className="mt-1">{website.smtpServer}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Adresse email</p>
                      <p className="mt-1">{website.smtpEmail || (website.emailAddresses && website.emailAddresses[0]) || 'Non spécifiée'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Port SMTP</p>
                      <p className="mt-1">{website.smtpPort}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Chiffrement</p>
                      <p className="mt-1">{website.encryption}</p>
                    </div>
                  </>
                )}
                
                {/* Affichage pour Postal HTTP */}
                {website.emailType === 'postal-http' && (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">URL du serveur</p>
                      <p className="mt-1">{website.postalHttpUrl || 'Non spécifiée'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email d'expéditeur</p>
                      <p className="mt-1">{website.postalHttpSenderEmail || 'Non spécifié'}</p>
                    </div>
                  </>
                )}
                
                {/* Affichage pour le type Autre */}
                {website.emailType === 'autre' && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Notes</p>
                    <p className="mt-1 whitespace-pre-wrap">{website.emailNote || 'Aucune note'}</p>
                  </div>
                )}
                
                {/* Affichage des adresses email pour la compatibilité */}
                {!website.emailType && website.emailAddresses && website.emailAddresses.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Adresses email</p>
                    <div className="mt-1">
                      <ul className="list-disc pl-5 space-y-1">
                        {website.emailAddresses.map((email, index) => (
                          <li key={index}>{email}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Maintenance et services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Type de backup</p>
                  <p className="mt-1 flex items-center">
                    {website.backupType === 'minio' ? (
                      <>
                        <span>Bucket Minio</span>
                        {usesMinio() && (
                          <img 
                            src="/icons/minio-icon.svg" 
                            alt="Utilise Minio" 
                            className="w-5 h-5 ml-2"
                            aria-label="Ce site utilise Minio pour les backups"
                          />
                        )}
                      </>
                    ) : website.backupType === 'docker' ? (
                      'Serveur Docker'
                    ) : website.backupType === 'autre' ? (
                      `Autre (${website.backupNote || 'non spécifié'})`
                    ) : (
                      'Sans backup'
                    )}
                  </p>
                </div>
                
                {website.backupType === 'minio' && (
                  <>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nom du bucket</p>
                      <p className="mt-1">{website.bucketName || 'Non spécifié'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Créneau de backup</p>
                      <p className="mt-1">
                        {website.backupSchedule?.DayOfMonth !== undefined 
                          ? `Tranche: ${website.backupSchedule.DayOfMonth}` 
                          : 'Non spécifié'}
                        {website.backupSchedule?.time 
                          ? `, Heure: ${website.backupSchedule.time}` 
                          : ''}
                      </p>
                    </div>
                  </>
                )}
                
                {website.backupType === 'autre' && (
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Notes sur le backup</p>
                    <p className="mt-1 whitespace-pre-wrap">{website.backupNote || 'Aucune note'}</p>
                  </div>
                )}
                
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Commentaire de maintenance</p>
                  <p className="mt-1 whitespace-pre-wrap">{website.maintenanceComments || 'Aucun commentaire'}</p>
                </div>
              </div>
              
              <div className="space-y-4">
                {website.backupType !== 'aucun' && (
                  <div className="border rounded-md p-4 bg-gray-50 dark:bg-gray-800">
                    <h3 className="text-lg font-medium mb-3">Informations de backup</h3>
                    
                    {website.backupType === 'minio' && (
                      <div className="mb-4">
                        <button 
                          onClick={checkBackup}
                          disabled={checkingBackup}
                          className="btn-primary flex items-center"
                        >
                          {checkingBackup ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                              Vérification...
                            </>
                          ) : (
                            <>
                              <CheckCircleIcon className="h-5 w-5 mr-2" />
                              Vérifier le backup
                            </>
                          )}
                        </button>
                      </div>
                    )}
                    
                    {website.backupType === 'minio' && website.backupDetails ? (
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nom du bucket</p>
                          <p className="mt-1 font-mono">{website.backupDetails.bucketName}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Dernier backup</p>
                          <p className="mt-1">
                            <span className={getBackupDateColorClass(website.backupDetails.lastBackupDate)}>
                              {new Date(website.backupDetails.lastBackupDate).toLocaleString()}
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Taille du backup</p>
                          <p className="mt-1">{website.backupDetails.backupSize}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nombre d'objets</p>
                          <p className="mt-1">{website.backupDetails.objectCount}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Statut</p>
                          <p className="mt-1">
                            <span 
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                website.backupStatus === 'recent' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                                  : website.backupStatus === 'warning'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                    : website.backupStatus === 'danger'
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                              }`}
                            >
                              {website.backupStatus === 'recent' 
                                ? 'Récent' 
                                : website.backupStatus === 'warning'
                                  ? 'À vérifier'
                                  : website.backupStatus === 'danger'
                                    ? 'Ancien'
                                    : 'Inconnu'}
                            </span>
                          </p>
                        </div>
                      </div>
                    ) : website.backupType === 'minio' ? (
                      <p className="text-gray-500 dark:text-gray-400 italic">
                        {website.backupStatus === 'not_found' 
                          ? 'Aucun backup trouvé pour ce site' 
                          : 'Cliquez sur "Vérifier le backup" pour obtenir des informations'}
                      </p>
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 italic">
                        {website.backupType === 'docker' 
                          ? 'Backup géré par le serveur Docker' 
                          : 'Voir les notes pour plus d\'informations'}
                      </p>
                    )}
                    
                    {website.backupType === 'minio' && website.backupSchedule && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h4 className="text-md font-medium mb-2">Planification des backups</h4>
                        <div className="space-y-2">
                          <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Tranche horaire</p>
                            <p className="mt-1">Tranche {website.backupSchedule.DayOfMonth}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Heure</p>
                            <p className="mt-1">{website.backupSchedule.time}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Contacts</h2>
            
            {website.contacts && website.contacts.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2">
                {website.contacts.map((contact, index) => (
                  <div key={contact.id || index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
                    {contact.isPrimary && (
                      <div className="mb-2">
                        <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                          Contact Principal
                        </span>
                      </div>
                    )}
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Nom</p>
                        <p className="mt-1 font-medium">{contact.name || 'Non spécifié'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</p>
                        <p className="mt-1">
                          {contact.email ? (
                            <a href={`mailto:${contact.email}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                              {contact.email}
                            </a>
                          ) : (
                            'Non spécifié'
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Téléphone</p>
                        <p className="mt-1">
                          {contact.phone ? (
                            <a href={`tel:${contact.phone}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                              {contact.phone}
                            </a>
                          ) : (
                            'Non spécifié'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Afficher les anciens champs de contact pour compatibilité
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Contact principal</p>
                    <p className="mt-1">{website.primaryContact || 'Non spécifié'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email du contact</p>
                    <p className="mt-1">
                      {website.contactEmail ? (
                        <a href={`mailto:${website.contactEmail}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                          {website.contactEmail}
                        </a>
                      ) : (
                        'Non spécifié'
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Téléphone du contact</p>
                    <p className="mt-1">
                      {website.contactPhone ? (
                        <a href={`tel:${website.contactPhone}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                          {website.contactPhone}
                        </a>
                      ) : (
                        'Non spécifié'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'wordpress' && (
          <div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Informations WordPress</h2>
            
            <div className="mb-6">
              <button 
                onClick={detectWordPressPlugins}
                disabled={detectingPlugins}
                className="btn-primary flex items-center"
              >
                {detectingPlugins ? (
                  <>
                    <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                    Détection des plugins en cours...
                  </>
                ) : (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                    Détecter les plugins
                  </>
                )}
              </button>
            </div>
            
            {pluginsData ? (
              <div className="space-y-6">
                {pluginsData.pluginsResult.detected ? (
                  <>
                    {/* Affichage des informations sur les plugins */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium mb-4">Plugins détectés</h3>
                      
                      {pluginsData.pluginsResult.plugins.length > 0 ? (
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Plugins actifs ({pluginsData.pluginsResult.activePlugins.length})</p>
                            <ul className="mt-2 list-disc pl-5">
                              {pluginsData.pluginsResult.activePlugins.map((plugin: string, index: number) => (
                                <li key={index} className="text-sm">{plugin}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400">Aucun plugin n'a été détecté</p>
                      )}
                    </div>
                    
                    {/* Affichage spécifique pour les plugins de cookies */}
                    {pluginsData.pluginsResult.cookiePlugin && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-4">
                        <h3 className="text-lg font-medium mb-4">Gestion des cookies</h3>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Plugin détecté</p>
                          <p className="mt-1 text-sm">{pluginsData.pluginsResult.cookiePlugin.name}</p>
                        </div>
                        {pluginsData.pluginsResult.hasCookieBanner && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Bandeau de cookies</p>
                            <p className="mt-1 text-sm text-green-600 dark:text-green-400">Détecté sur la page d'accueil</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Affichage pour Turnstile */}
                    {website.detectionResults && 
                     website.detectionResults.wordpress && 
                     website.detectionResults.wordpress.subProbes && 
                     website.detectionResults.wordpress.subProbes.turnstile && (
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-4">
                        <h3 className="text-lg font-medium mb-4">Cloudflare Turnstile</h3>
                        {website.detectionResults.wordpress.subProbes.turnstile.detected ? (
                          <>
                            <div className="flex items-center mb-4">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-green-600 dark:text-green-400 font-medium">Turnstile détecté</span>
                            </div>
                            
                            {website.detectionResults.wordpress.subProbes.turnstile.plugin && (
                              <div className="mt-2">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Plugin identifié</p>
                                <p className="mt-1 text-sm">{website.detectionResults.wordpress.subProbes.turnstile.plugin.name}</p>
                              </div>
                            )}
                            
                            <div className="mt-4">
                              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">État de configuration</p>
                              {website.detectionResults.wordpress.subProbes.turnstile.isConfigured ? (
                                <div className="flex items-center mt-1">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  <span className="text-sm text-green-600 dark:text-green-400">Correctement configuré</span>
                                </div>
                              ) : (
                                <div className="flex items-center mt-1">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  <span className="text-sm text-yellow-600 dark:text-yellow-400">Installation détectée mais non configurée</span>
                                </div>
                              )}
                            </div>
                            
                            {website.detectionResults.wordpress.subProbes.turnstile.recommendation && (
                              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-md">
                                <p className="text-sm text-blue-800 dark:text-blue-200">{website.detectionResults.wordpress.subProbes.turnstile.recommendation}</p>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="flex items-center mb-4">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              <span className="text-red-600 dark:text-red-400 font-medium">Turnstile non détecté</span>
                            </div>
                            
                            {/* Informations sur les formulaires détectés */}
                            {(website.detectionResults.wordpress.subProbes.turnstile.hasContactForm ||
                              website.detectionResults.wordpress.subProbes.turnstile.hasCommentForm ||
                              website.detectionResults.wordpress.subProbes.turnstile.hasLoginForm) && (
                              <div className="mt-2">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Formulaires détectés</p>
                                <ul className="mt-1 pl-5 list-disc text-sm">
                                  {website.detectionResults.wordpress.subProbes.turnstile.hasContactForm && (
                                    <li>Formulaire de contact</li>
                                  )}
                                  {website.detectionResults.wordpress.subProbes.turnstile.hasCommentForm && (
                                    <li>Formulaire de commentaire</li>
                                  )}
                                  {website.detectionResults.wordpress.subProbes.turnstile.hasLoginForm && (
                                    <li>Formulaire de connexion</li>
                                  )}
                                </ul>
                              </div>
                            )}
                            
                            {website.detectionResults.wordpress.subProbes.turnstile.recommendation && (
                              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900 rounded-md">
                                <p className="text-sm text-blue-800 dark:text-blue-200">{website.detectionResults.wordpress.subProbes.turnstile.recommendation}</p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-yellow-50 dark:bg-yellow-900 p-4 rounded-md">
                    <p className="text-yellow-800 dark:text-yellow-200">
                      {pluginsData.pluginsResult.error || "Ce site n'utilise pas WordPress ou n'a pas été correctement détecté."}
                    </p>
                  </div>
                )}
                
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Dernière vérification : {new Date(pluginsData.pluginsResult.lastScanned).toLocaleString()}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-md">
                <p className="text-gray-600 dark:text-gray-300">
                  Cliquez sur le bouton "Détecter les plugins" pour analyser ce site web et identifier les plugins WordPress installés.
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Section Cloudflare */}
        {website.detectionResults && 
         website.detectionResults.cloudflare && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-4">
            <h3 className="text-lg font-medium mb-4">Protection Cloudflare</h3>
            {website.detectionResults.cloudflare.detected ? (
              <>
                <div className="flex items-center mb-4">
                  <img 
                    src="/icons/cloudflare.svg" 
                    alt="Cloudflare" 
                    className="w-6 h-6 mr-2"
                  />
                  <span className="text-orange-600 dark:text-orange-400 font-medium">Site protégé par Cloudflare</span>
                </div>
                
                {website.detectionResults.cloudflare.details && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {website.detectionResults.cloudflare.details.cfRay && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">CF-Ray ID</p>
                        <p className="mt-1 text-sm font-mono">{website.detectionResults.cloudflare.details.cfRay}</p>
                      </div>
                    )}
                    
                    {website.detectionResults.cloudflare.details.server && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Serveur</p>
                        <p className="mt-1 text-sm">{website.detectionResults.cloudflare.details.server}</p>
                      </div>
                    )}
                    
                    {website.detectionResults.cloudflare.details.cacheStatus && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Statut du cache</p>
                        <p className="mt-1 text-sm">{website.detectionResults.cloudflare.details.cacheStatus}</p>
                      </div>
                    )}
                    
                    {website.detectionResults.cloudflare.details.country && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pays de connexion</p>
                        <p className="mt-1 text-sm">{website.detectionResults.cloudflare.details.country}</p>
                      </div>
                    )}
                    
                    {website.detectionResults.cloudflare.details.ipAddress && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Adresse IP Cloudflare</p>
                        <p className="mt-1 text-sm font-mono">{website.detectionResults.cloudflare.details.ipAddress}</p>
                      </div>
                    )}
                    
                    {website.detectionResults.cloudflare.details.nameservers && website.detectionResults.cloudflare.details.nameservers.length > 0 && (
                      <div className="md:col-span-2">
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Serveurs DNS</p>
                        <div className="mt-1 text-sm">
                          {website.detectionResults.cloudflare.details.nameservers.map((ns: string, index: number) => (
                            <span key={index} className="inline-block bg-gray-100 dark:bg-gray-700 rounded px-2 py-1 mr-2 mb-1 font-mono text-xs">
                              {ns}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {website.detectionResults.cloudflare.methods && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Méthodes de détection</p>
                    <div className="flex flex-wrap gap-2">
                      {website.detectionResults.cloudflare.methods.headers && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                          Headers HTTP
                        </span>
                      )}
                      {website.detectionResults.cloudflare.methods.dns && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                          Serveurs DNS
                        </span>
                      )}
                      {website.detectionResults.cloudflare.methods.ip && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                          Plage IP
                        </span>
                      )}
                      {website.detectionResults.cloudflare.methods.rayId && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300">
                          CF-Ray ID
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {website.detectionResults.cloudflare.recommendation && (
                  <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      {website.detectionResults.cloudflare.recommendation}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="text-gray-600 dark:text-gray-400 font-medium">Cloudflare non détecté</span>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Modal de confirmation de suppression */}
      {deleteModalOpen && website && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Confirmer la suppression
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Êtes-vous sûr de vouloir supprimer ce site web ? Cette action est irréversible.
              </p>
              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                <p className="font-medium text-gray-900 dark:text-white">{website.clientName}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">{website.url}</p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors flex items-center"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Suppression...
                  </>
                ) : (
                  'Supprimer'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteDetail; 